import * as React from "react";
import {
  canComment as accessCanComment,
  canEdit as accessCanEdit,
  type AccessLevel,
} from "#app/lib/utils/access-level";

// Per-issue capability flags for the open issue. Provided by the issue body
// once `useIssueViewData` resolves and consumed by edit / note screens to
// gate input. `canEdit` is the SERVER's edit decision (`Issue.viewerCanEdit`:
// the role floor and the object's access level together, the same decision
// every edit mutation makes) — so an editor the drawer offers is a write the
// server accepts. `level` / `canComment` derive from the displayed access
// tier, which drives commenting and sharing. Default values are permissive
// (true) so the live query loading state doesn't briefly disable controls —
// the authoritative backend mutations still block unauthorized writes.
export interface IssueViewCapabilities {
  level: AccessLevel | null;
  canEdit: boolean;
  canComment: boolean;
}

const DEFAULT_CAPS: IssueViewCapabilities = {
  level: null,
  canEdit: true,
  canComment: true,
};

const Ctx = React.createContext<IssueViewCapabilities>(DEFAULT_CAPS);

export function IssueViewCapabilitiesProvider({
  level,
  canEdit,
  children,
}: {
  level: AccessLevel | null | undefined;
  /** `Issue.viewerCanEdit`; `undefined` while the issue is unresolved. */
  canEdit: boolean | undefined;
  children: React.ReactNode;
}) {
  const value = React.useMemo<IssueViewCapabilities>(() => {
    // While the per-issue caps are unresolved (initial load), keep the
    // defaults — the backend will reject if it shouldn't have gone through
    // anyway. Once values come back we lock down.
    if (level === undefined && canEdit === undefined) return DEFAULT_CAPS;
    const normalized = (level ?? null) as AccessLevel | null;
    return {
      level: normalized,
      // A resolved level with no edit decision yet (a row from an older
      // cache shape) falls back to the tier, never to the permissive default.
      canEdit: canEdit ?? accessCanEdit(normalized),
      canComment: accessCanComment(normalized),
    };
  }, [level, canEdit]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIssueViewCapabilities(): IssueViewCapabilities {
  return React.useContext(Ctx);
}
